<template>
  <div class="calendar-view">
    <h1 class="calendar-view__title">Availability of {{ teacherName }}</h1>
    <header class="calendar-view__header">
      <div class="calendar-view__start">
        <VoxyButton view="primary" size="s" @click="navigateBack"> &lt; </VoxyButton>
      </div>
      <VoxyButton view="primary" :disabled="!events.length" size="s" @click="openReplicateModal">
        Replicate Availability
      </VoxyButton>
    </header>
    <VoxyCalendar
      :timezone="profile.timezone"
      :events="events"
      :start-time="lastInfo.start"
      :end-time="lastInfo.end"
      @event-click="deleteSlot"
      @event-add="addSlots"
      @event-fetch="fetchSlots"
    />
    <SuccessModal
      v-if="successModalIsOpen"
      title="Availability replicated successfully"
      @modal:confirm="toggleSuccessModal"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import { VoxyButton, VoxyCalendar, SuccessModal } from '@voxy-team/vue-voxy';
import CalendarReplicateModal from './components/CalendarReplicateModal';

export const modalParams = {
  classes: 'v--modal v--modal_theme_dark',
  clickToClose: false,
  height: 'auto',
  width: 400,
  scrollable: false,
};

export default {
  name: 'MyCalendar',

  components: { VoxyButton, VoxyCalendar, SuccessModal },

  async mounted() {
    await this.getTeacherName();
  },

  data() {
    return {
      teacherName: null,
      successModalIsOpen: false,
      events: [],
      routeParams: {
        teacherHashEmail: this.$route.params.teacherHashEmail,
        orgId: this.$route.params.orgId,
        isAdmin: this.$route.name === 'manage_teachers_calendar',
      },
      lastInfo: {
        start: new Date(),
        end: new Date(),
      },
    };
  },

  computed: {
    ...mapState('manageTeachers', ['teachers']),
    ...mapGetters(['userProfile']),
  },

  methods: {
    ...mapActions('schedule', {
      fetchAvailableSlots: 'FETCH_AVAILABLE_SLOTS',
      addAvailableSlots: 'ADD_AVAILABLE_SLOTS',
      deleteAvailableSlots: 'DELETE_AVAILABLE_SLOTS',
      replicateWeeks: 'REPLICATE_WEEKS',
    }),
    ...mapActions('manageTeachers', {
      fetchTeacherProfile: 'FETCH_TEACHER_PROFILE',
    }),
    ...mapGetters({
      profile: 'userProfile',
    }),

    navigateBack() {
      if (this.routeParams.isAdmin) {
        this.$router.push({
          name: 'teachers',
        });
        return;
      }

      this.$router.push({
        name: 'my_schedule',
      });
    },

    async addSlots(info) {
      try {
        const infoWithRouterParams = {
          ...info,
          teacherHashEmail: this.routeParams.teacherHashEmail,
          orgId: this.routeParams.orgId,
          isAdmin: this.routeParams.isAdmin,
        };

        await this.addAvailableSlots(infoWithRouterParams);
      } finally {
        this.fetchSlots(this.lastInfo.start, this.lastInfo.end);
      }
    },

    async deleteSlot(info) {
      try {
        const infoWithRouterParams = {
          ...info,
          teacherHashEmail: this.routeParams.teacherHashEmail,
        };

        await this.deleteAvailableSlots(infoWithRouterParams);
      } finally {
        this.fetchSlots(this.lastInfo.start, this.lastInfo.end);
      }
    },

    async fetchSlots(start, end) {
      this.lastInfo.start = start;
      this.lastInfo.end = end;

      try {
        const data = await this.fetchAvailableSlots({
          start,
          end,
          teacherHashEmail: this.routeParams.teacherHashEmail,
        });
        this.events = data;
      } catch (e) {
        this.events = [];
      }
    },

    async replicateAvailability(weeks) {
      const infoWithRouterParams = {
        start: this.lastInfo.start,
        weeks,
        teacherHashEmail: this.routeParams.teacherHashEmail,
        orgId: this.routeParams.orgId,
        isAdmin: this.routeParams.isAdmin,
      };
      await this.replicateWeeks(infoWithRouterParams);
      this.toggleSuccessModal();
    },

    openReplicateModal() {
      this.$modal.show(CalendarReplicateModal, { confirmAction: this.replicateAvailability }, modalParams);
    },

    toggleSuccessModal() {
      this.successModalIsOpen = !this.successModalIsOpen;
    },

    async getTeacherName() {
      if (!this.routeParams.isAdmin) {
        this.teacherName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
        return;
      }

      const { name } = await this.fetchTeacherProfile(this.routeParams.teacherHashEmail);
      this.teacherName = name;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/tools/mixins';

.calendar-view {
  &__start {
    display: flex;
    gap: 16px;
  }

  &__header {
    display: flex;
    margin-bottom: 12px;
    justify-content: space-between;
  }

  &__title {
    @include pageTitle;

    margin-bottom: 12px;
  }
}
</style>
